import moment from 'moment-timezone';
import 'moment/locale/es';
import 'moment/locale/es-mx';
import 'moment/locale/es-us';
import 'moment/locale/en-gb';
import 'moment/locale/pt';
import 'moment/locale/pt-br';
import { getDefaultLanguage } from './browser';
import { USER_LANGUAGES } from '../assets/AppConstants';

export const addDate = (date, amount, dateType) => {
  if (dateType === 'days') {
    date.setDate(date.getDate() + amount);
  }

  if (dateType === 'weeks') {
    date.setDate(date.getDate() + (7 * amount));
  }

  if (dateType === 'months') {
    date.setMonth(date.getMonth() + amount);
  }

  if (dateType === 'years') {
    date.setFullYear(date.getFullYear() + amount);
  }

  return date;
};

const getLangToDate = () => {
  let language = getDefaultLanguage().id;
  const userString = localStorage.getItem('user');
  if (userString && userString !== 'null') {
    const useUser = JSON.parse(userString);
    if (useUser?.language) language = useUser.language;
  }

  return USER_LANGUAGES.find((el) => el.id.toLocaleLowerCase() === language.toLocaleLowerCase());
};

export const formatDate = (date, format = 'L LTS') => {
  const lang = getLangToDate()?.name?.toLowerCase();

  if (['es', 'pt'].includes(lang)) {
    moment.updateLocale(lang, {
      longDateFormat: {
        ll: 'D MMM YYYY',
        lll: 'D MMM YYYY HH:mm',
        L: 'DD/MM/YYYY',
      },
    });
  } else if (['en'].includes(lang)) {
    moment.updateLocale(lang, {
      longDateFormat: {
        LTS: 'HH:mm:ss',
      },
    });
  }

  moment.locale(lang);

  return moment(date).format(format);
};

export const formatDateFromNow = (date) => {
  moment.locale(getLangToDate().name.toLowerCase());
  return moment(date).startOf('hour').fromNow();
};

export const isTodayOrFuture = (dateFrom) => moment(moment().format('YYYY-MM-DD')).diff(moment(dateFrom).format('YYYY-MM-DD')) <= 0;

export const getTodayDate = () => moment().format('YYYY-MM-DD');

export const getDate = (date, format = 'L') => moment(date).format(format);

export const todayIsBefore = (date) => {
  const todayDate = moment(moment().format('YYYY-MM-DD'));
  const pastDate = moment(date, 'YYYY-MM-DD');

  return todayDate.isBefore(pastDate);
};

export const toTimeString = (date) => {
  const dateTime = new Date(date);

  return moment(dateTime).format('HH:mm ');
};

export const toDateTimeString = (date) => {
  const dateTime = new Date(date);

  return moment(dateTime).format('YYYY-MM-DD HH:mm');
};

export const getNow = (config = {}) => {
  const { format = 'YYYY-MM-DD' } = config;
  const lang = getLangToDate().name.toLowerCase();
  moment.locale(lang);
  return moment().format(format);
};

export const getDayName = (date) => {
  const lang = getLangToDate().name.toLowerCase();
  moment.locale(lang);
  return moment(date).format('dddd');
};

export const getDayNumber = (date) => {
  const lang = getLangToDate().name.toLowerCase();
  moment.locale(lang);
  return moment(date).format('DD');
};

export const getWeekday = (date) => {
  const lang = getLangToDate().name.toLowerCase();
  moment.locale(lang);

  return moment(date).day();
};

export const timesList = () => {
  const start = moment().startOf('day');
  const times = 24 * 4; // 24 hours * 15 mins in an hour
  const items = [];

  for (let i = 0; i < times; i += 1) {
    const time = moment(start).add(15 * i, 'minutes');
    const value = time.format('HH:mm:ss');
    const name = time.format('HH:mm ');

    items.push({ name, value });
  }

  return items;
};

export const dateToDB = (date) => moment(date, 'YYYY-MM-DD HH:mm').toISOString();

export const getMoment = () => {
  const lang = getLangToDate()?.name?.toLowerCase();

  if (['es', 'pt'].includes(lang)) {
    moment.updateLocale(lang, {
      longDateFormat: {
        ll: 'D MMM YYYY',
        lll: 'D MMM YYYY HH:mm',
      },
      calendar: {
        sameElse: 'D MMM YYYY HH:mm',
      },
    });
  } else if (['en'].includes(lang)) {
    moment.updateLocale(lang, {
      longDateFormat: {
        LTS: 'HH:mm:ss',
      },
      calendar: {
        sameElse: 'D MMM YYYY HH:mm',
      },
    });
  }

  moment.locale(lang);

  return moment;
};

export const enumerateDaysBetweenDates = (startDate, endDate) => {
  const dates = [];

  const currDate = moment(startDate).startOf('day');
  const lastDate = moment(endDate).startOf('day');

  dates.push({
    date: moment(startDate),
    dateType: 'DAILY',
  });

  while (currDate.add(1, 'days').diff(lastDate) < 0) {
    dates.push({
      date: currDate.clone(),
      dateType: 'DAILY',
    });
  }
  if (startDate !== endDate) {
    dates.push({
      date: moment(endDate),
      dateType: 'DAILY',
    });
  }

  return dates;
};

export const enumerateMonthsBetweenDates = (date1, date2) => {
  const startDate = moment(date1);
  const endDate = moment(date2);
  const betweenMonths = [];

  if (startDate <= endDate) {
    const date = startDate.startOf('month');

    while (date < endDate.endOf('month')) {
      betweenMonths.push({
        date: date.clone(),
        dateType: 'MONTHLY',
      });

      if (date.format('MM') === '12') {
        betweenMonths.push({
          date: date.clone(),
          dateType: 'ANNUAL',
        });
      }

      date.add(1, 'month');
    }
  }

  return betweenMonths;
};

export const enumerateYearsBetweenDates = (date1, date2) => {
  const startDate = moment(date1);
  const endDate = moment(date2);
  const betweenMonths = [];

  if (startDate <= endDate) {
    const date = startDate.startOf('month');
    while (date < endDate.endOf('month')) {
      if (date.format('MM') === '12') {
        betweenMonths.push(date.clone().format('YYYY'));
      }
      date.add(1, 'month');
    }
  }

  return betweenMonths;
};

export const millisToMinutesAndSeconds = (millis) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = ((millis % 60000) / 1000).toFixed(0);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const listFormat = (list, options = { style: 'long', type: 'conjunction' }) => {
  const lang = getLangToDate()?.name?.toLowerCase();
  const formatter = new Intl.ListFormat(lang, options);

  return formatter.format(list);
};

export const getWeekRanges = (month) => {
  const ranges = [];
  const startDate = moment(month, 'YYYY-MM').startOf('month');

  if (startDate.day() !== 1) {
    startDate.add(1, 'week').day(1);
  }

  const endDate = moment(month, 'YYYY-MM').endOf('month');

  while (startDate.isBefore(endDate)) {
    const weekStart = startDate.clone();
    let weekEnd = weekStart.clone().day(5);

    if (weekEnd.isAfter(endDate)) {
      weekEnd = endDate.clone();
    }

    ranges.push(`${weekStart.format('YYYY-MM-DD')} - ${weekEnd.format('YYYY-MM-DD')}`);

    startDate.add(1, 'week');
  }

  return ranges;
};

// eslint-disable-next-line import/no-unused-modules
export const getCountDays = (dateFrom, dateTo) => moment(dateTo).diff(moment(dateFrom), 'days');

export const isWeekend = (date) => [0, 6].includes(moment(date).day());

export const isHoliday = (date, holidays = []) => !!holidays.find((el) => el === date);

export const getCountWorkingDays = (dateFrom, dateTo, holidays) => {
  let days = 0;

  let currDate = moment(dateFrom).add(1, 'days');
  const lastDate = moment(dateTo).add(1, 'days');

  while (currDate.diff(lastDate) < 0) {
    const formatedDate = currDate.format('YYYY-MM-DD');
    if (!isHoliday(formatedDate, holidays) && !isWeekend(formatedDate)) {
      days += 1;
    }

    currDate = currDate.add(1, 'days');
  }

  return days;
};
